import { Alert, Button, Card, Descriptions, Select, Space, Tag, message } from 'antd';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '@/services/api.service';
import { useAuthStore } from '@/store/authStore';
import EducationPageChrome from '@/pages/education/components/EducationPageChrome';

type Accommodation = {
  id: string;
  accommodation_no: string;
  category: string;
  summary: string;
  detail_text?: string | null;
  effective_from: string;
  effective_to?: string | null;
  status: string;
  record_version: number;
  admission_no?: string;
  first_name?: string;
  last_name?: string;
  section_name?: string;
};

export default function InclusionAccommodationDetailPage() {
  const { t } = useTranslation('education');
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const user = useAuthStore((s) => s.user);
  const canManage = Boolean(
    user?.permissions?.includes('education.inclusion.manage') || user?.permissions?.includes('education.admin')
  );

  const query = useQuery({
    queryKey: ['education-inclusion-accommodation', id],
    enabled: Boolean(id),
    queryFn: async () => (await api.get<Accommodation>(`/education/inclusion/accommodations/${id}`)).data,
  });
  const row = query.data;
  const open = row && row.status === 'active';

  const setStatus = useMutation({
    mutationFn: (status: string) =>
      api.post(`/education/inclusion/accommodations/${id}/status`, {
        status,
        record_version: row?.record_version,
      }),
    onSuccess: () => {
      message.success(t('accommodationStatusUpdated'));
      queryClient.invalidateQueries({ queryKey: ['education-inclusion-accommodation', id] });
      queryClient.invalidateQueries({ queryKey: ['education-inclusion-accommodations'] });
    },
    onError: (error: { response?: { data?: { message?: string } } }) =>
      message.error(error.response?.data?.message || t('unavailable', { status: '' })),
  });

  return (
    <EducationPageChrome
      title={row?.accommodation_no || t('inclusionAccommodations')}
      description={t('inclusionAccommodationsIntro')}
      tags={row ? <Tag>{row.status}</Tag> : null}
      extra={
        <Button onClick={() => navigate('/education/inclusion/accommodations')}>{t('inclusionAccommodations')}</Button>
      }
    >
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Alert type="info" showIcon message={t('inclusionNonDiagnosticHint')} />
        <Card size="small" loading={query.isLoading}>
          {row ? (
            <Descriptions column={{ xs: 1, md: 2 }} size="small">
              <Descriptions.Item label={t('student')}>
                {`${row.admission_no || ''} — ${row.first_name || ''} ${row.last_name || ''}`.trim()}
              </Descriptions.Item>
              <Descriptions.Item label={t('section')}>{row.section_name || '—'}</Descriptions.Item>
              <Descriptions.Item label={t('category')}>{row.category}</Descriptions.Item>
              <Descriptions.Item label={t('effectiveFrom')}>{row.effective_from}</Descriptions.Item>
              <Descriptions.Item label={t('effectiveTo')}>{row.effective_to || '—'}</Descriptions.Item>
              <Descriptions.Item label={t('summary')} span={2}>
                {row.summary}
              </Descriptions.Item>
              <Descriptions.Item label={t('detail')} span={2}>
                {row.detail_text || '—'}
              </Descriptions.Item>
            </Descriptions>
          ) : null}
        </Card>
        {canManage && open ? (
          <Card size="small" title={t('status')}>
            <Select
              style={{ minWidth: 200 }}
              placeholder={t('status')}
              options={[
                { value: 'ended', label: t('statusEnded') },
                { value: 'cancelled', label: t('statusCancelled') },
              ]}
              onChange={(v) => setStatus.mutate(v)}
            />
          </Card>
        ) : null}
      </Space>
    </EducationPageChrome>
  );
}
